// ============================================================
// ASTROLOG — Moteur d'estimation (distance + tarifs)
// Distance : géocodage Nominatim + Haversine × 1,25 (sans clé API)
// ============================================================
import { PRICING_RULES } from "@/config/site";

export function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// Géocodage via Nominatim (OpenStreetMap) — aucune clé requise.
// Aucune donnée n'est transmise sans saisie explicite de l'utilisateur.
export async function geocodeAddress(query, signal) {
  const params = new URLSearchParams({
    format: "jsonv2",
    q: query,
    limit: "5",
    countrycodes: "fr,be,lu,nl,de,es,it,pt,ch",
    "accept-language": "fr",
    addressdetails: "1",
  });
  const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
    signal,
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error("Géocodage indisponible");
  const data = await res.json();
  return data.map((r) => ({
    label: r.display_name,
    lat: parseFloat(r.lat),
    lon: parseFloat(r.lon),
  }));
}

// Estimation de la fourchette de prix.
// Retourne { ok, distanceKm, billedKm, low, high, weightSupplement, error }
export function estimatePrice({ distanceKm, weightKg, vehicle, options }) {
  if (!vehicle) return { ok: false, error: "Véhicule non sélectionné." };
  if (!distanceKm || distanceKm <= 0)
    return { ok: false, error: "Distance non calculable. Vérifiez les adresses saisies." };

  const weight = Number(weightKg) || 0;
  if (weight > PRICING_RULES.maxWeightKg)
    return {
      ok: false,
      error: "Au-delà de 30 000 kg : nous consulter pour un transport exceptionnel.",
    };
  if (weight > vehicle.payload)
    return {
      ok: false,
      error: "Poids supérieur à la capacité de ce véhicule. Choisissez un véhicule plus grand.",
    };

  const billedKm = Math.max(distanceKm, PRICING_RULES.minDistanceKm);

  // Supplément poids
  const ratio = weight / vehicle.payload;
  let weightSupplement = 0;
  if (ratio > 0.8) weightSupplement = PRICING_RULES.weightSupplements.high;
  else if (ratio > 0.5) weightSupplement = PRICING_RULES.weightSupplements.mid;

  // Sous-total
  let low = vehicle.priseEnCharge + billedKm * vehicle.kmLow + weightSupplement;
  let high = vehicle.priseEnCharge + billedKm * vehicle.kmHigh + weightSupplement;

  // Options
  if (options?.demenagement) {
    low += low * PRICING_RULES.options.demenagement.lowPct;
    high += high * PRICING_RULES.options.demenagement.highPct;
  }
  if (options?.urgence) {
    low += low * PRICING_RULES.options.urgence.pct;
    high += high * PRICING_RULES.options.urgence.pct;
  }
  if (options?.hayon) {
    low += PRICING_RULES.options.hayon.flat;
    high += PRICING_RULES.options.hayon.flat;
  }

  return {
    ok: true,
    distanceKm: Math.round(distanceKm),
    billedKm,
    low: Math.round(low),
    high: Math.round(high),
    weightSupplement,
  };
}
